import { Injectable, inject } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounce } from 'lodash';

import { Account } from '../models/account.model';
import { ResetPinRequest, Person } from '../models/person.model';
import { Topic } from '@weavix/models/src/topic/topic';
import { LoggedInUser, UpdateUser, UserBadge, UserProfile, UserQrLogin } from '../models/user.model';
import { PermissionChecker } from '../permissions/permission-checker';
import { AlertService } from './alert.service';
import { FacilityService } from './facility.service';
import { HttpService } from './http.service';
import { PubSubService } from './pub-sub.service';
import { PermissionAction, PermissionApiResponse } from '@weavix/models/src/permission/permissions.model';
import { TranslationService } from './translation.service';
import { SyncNgService } from './sync/sync-ng-service';
import { TelemetryService } from './telemetry.service';
import { TelemetryEvent } from '@weavix/telemetry';
import { getRootStore } from 'models-mobx/root-store/root-store';
import { SyncService } from '@weavix/sync/src/sync.service';
import { clearSiteEntitlements, setSiteEntitlementsFallbackReporter, siteEntitlements, watchSiteEntitlements } from 'models-mobx/product-packages-store/site-entitlements';
import { environment } from 'environments/environment';
import { myUser } from 'models-mobx/users-store/users-store';
import { PushPayload } from '@weavix/models/src/push/push';
import { PersonFacilityPredicate, PersonLike } from '@weavix/models/src/person/person-matcher';
import { UploadResponse } from '@weavix/models/src/upload/upload';
import { i18nLocale } from '@weavix/models/src/translate/translate';
import { isLocale } from '@weavix/domain/src/translate/is-locale';

const THIRTY_MINUTES = 1_800_000;

@Injectable({
    providedIn: 'root',
})
export class ProfileService {
    constructor(
        private readonly httpService: HttpService,
        private readonly alertsService: AlertService,
        private readonly pubSubService: PubSubService,
        private readonly facilityService: FacilityService,
        private readonly translationService: TranslationService,
        private readonly syncService: SyncNgService,
    ) {
        setSiteEntitlementsFallbackReporter(payload => this.telemetryService.sendTelemetry(TelemetryEvent.ProductPackageEntitlementFallback, payload));
        SyncService.addTrigger('site-entitlements', () => {
            this.watchSiteEntitlements();
            return () => this.stopWatchingSitePackages();
        });
    }

    private readonly telemetryService = inject(TelemetryService);
    userProfile: UserProfile;
    /**
     * Counts sign-outs: a sign-in a sign-out overtakes stops at its next step and returns no one's profile, and each
     * session asks for its own profile rather than sharing a request still pending from the one before. */
    private logouts = 0;
    private permissions: PermissionApiResponse;
    private permissionChecker: PermissionChecker;
    private permissionCheckers: { [accountId: string]: PermissionChecker } = {};
    private accounts: Account[];
    private nextUserBadgeUpdateTicks: number;
    profileUpdateSubject = new Subject<UserProfile>();
    permissionSub: Subscription[] = [];
    localeSub: Subscription;
    permissionChange$: Subject<Date> = new Subject<Date>();
    /** Counts permission loads by sign-in or account switch, so a reload started before one is dropped. */
    private permissionsLoads = 0;
    /** The reload in flight, and whether another was asked for during it. See {@link requestPermissionsReload}. */
    private permissionsReload?: Promise<void>;
    private permissionsReloadQueued = false;
    /** A package change arrived before the first permissions loaded, whose response may predate it; reload once they do. */
    private reloadAfterFirstLoad = false;
    /** Stops forwarding synced entitlement changes into {@link permissionChange$}; started once the first permissions load. */
    private stopWatchingSiteEntitlements?: () => void;

    updateUserProfileBadges(badge: UserBadge) {
        this.userProfile.badges = badge;
    }

    async refreshMyBadge(component: any): Promise<UserBadge> {
        if (!this.userProfile) return;
        if (
            this.nextUserBadgeUpdateTicks !== undefined
            && this.nextUserBadgeUpdateTicks > Date.now()
            && this.userProfile.badges
        ) return this.userProfile.badges;

        this.nextUserBadgeUpdateTicks = Date.now() + THIRTY_MINUTES;
        this.userProfile.badges = await this.httpService.get<UserBadge>(component, '/core/me/badge-refresh');
        return this.userProfile.badges;
    }

    /**
     * Offers a reload into `locale` when it differs from the language shown now; `locale` is stored only once the user accepts.
     * Returns whether it prompted. A later prompt replaces an open one, whose dismissal counts as declining it.
     */
    promptRefreshIfLocaleChanged(locale: string | undefined): boolean {
        if (!isLocale(locale) || locale === this.translationService.getTranslateLocale()) return false;
        this.alertsService.sendPrompt({
            messageKey: 'configuration.user.locale-updated',
            buttonProps: { label: 'generics.refresh', icon: { faIcon: 'fas fa-sync' } },
        }).subscribe(resp => {
            if (resp) {
                console.log('reloading due to locale update');
                this.translationService.storeLocale(locale);
                this.reloadWindow();
            }
        });
        return true;
    }

    reloadWindow() {
        document.location.reload();
    }

    async getUserProfile(component, force: boolean = false): Promise<UserProfile> {
        if (this.userProfile && !force) return this.userProfile;
        try {
            const logouts = this.logouts;
            const signedOut = () => logouts !== this.logouts;
            const profile = await this.httpService.get<LoggedInUser>(component, '/core/me/user', { session: logouts });
            if (signedOut()) return this.noProfile();
            await this.pubSubService.loggedIn(profile.id);
            if (signedOut()) return this.noProfile();
            if (environment.radioApp) await this.syncService.onLoggedIn();
            if (signedOut()) return this.noProfile();

            const accounts = await this.httpService.get<Account[]>(component, '/core/me/accounts');
            if (signedOut()) return this.noProfile();
            const permissions = await this.httpService.get<PermissionApiResponse>(component, '/core/me/account/permissions-v2');
            if (signedOut()) return this.noProfile();
            this.accounts = accounts;
            this.permissions = permissions;
            this.permissionsLoads++;
            this.permissionChecker = siteAwareChecker(this.permissions, this.permissions?.grants?.[0]?.scope.accountId);
            if (this.reloadAfterFirstLoad) {
                this.reloadAfterFirstLoad = false;
                this.requestPermissionsReload();
            }

            if (profile.locale) this.translationService.setTranslateLocale(profile.locale);
            if (this.localeSub) this.localeSub.unsubscribe();
            this.localeSub = this.pubSubService.subscribe<i18nLocale | { locale: i18nLocale }>(null, Topic.UserLocaleChange, [profile.id]).subscribe(payload => {
                const locale = typeof payload.payload === 'string'
                    ? payload.payload
                    : payload.payload?.locale;
                this.promptRefreshIfLocaleChanged(locale);
            });
            if (this.permissionSub.length > 0) await Promise.all(this.permissionSub.map(x => x.unsubscribe()));
            if (environment.radioApp) {
                await Promise.all(this.accounts.map(async account => {
                    await this.initAccountPermissionChecker(component, account.id, profile.id);
                }));
                console.log('this.permissions :>> ', this.permissions);
            }

            if (signedOut()) return this.noProfile();
            this.userProfile = profile;
            return this.userProfile;
        } catch (e) {
            this.alertsService.sendError({ error: e, messageKey: 'ERRORS.PROFILE.GET' });
            this.alertsService.setAppLoading(false);
            console.error(e);

            return this.noProfile();
        }
    }

    /** The profile of no one, returned when the profile could not be loaded or the user signed out while it loaded. */
    private noProfile(): UserProfile {
        return {
            id: null,
            avatarFile: null,
            firstName: null,
            lastName: null,
        };
    }

    onLogout(): void {
        this.stopWatchingSitePackages();
        this.logouts++;
        clearSiteEntitlements();
        if (this.permissionSub.length > 0) {
            this.permissionSub.forEach(x => x.unsubscribe());
            this.permissionSub = [];
        }
        if (this.localeSub) {
            this.localeSub.unsubscribe();
            this.localeSub = undefined;
        }
        this.userProfile = null;
    }

    isGlobalAdmin(): boolean {
        return this.permissionChecker.isGlobalAdmin();
    }

    /**
     * Announces synced package changes and reloads permissions after them. Started by the sync trigger once sign-in has
     * created the stores it watches, since their contexts are not observable and a watch started before them would track nothing.
     */
    private watchSiteEntitlements(): void {
        if (this.stopWatchingSiteEntitlements) return;
        getRootStore();
        this.stopWatchingSiteEntitlements = watchSiteEntitlements(() => {
            this.permissionChange$.next(new Date());
            this.reloadPermissionsDebounced();
        });
    }

    /** Stops the watch and any reload it scheduled. */
    private stopWatchingSitePackages(): void {
        this.stopWatchingSiteEntitlements?.();
        this.stopWatchingSiteEntitlements = undefined;
        this.reloadPermissionsDebounced.cancel();
        this.permissionsReloadQueued = false;
        this.reloadAfterFirstLoad = false;
    }

    private readonly reloadPermissionsDebounced = debounce(() => this.requestPermissionsReload(), 3000);

    /**
     * Runs a permissions reload, or queues one more if one is in flight: an in-flight GET is shared, so its response may predate
     * the change that asked for this one.
     */
    private requestPermissionsReload(): void {
        if (this.permissionsReload !== undefined) {
            this.permissionsReloadQueued = true;
            return;
        }
        this.permissionsReload = this.reloadPermissions()
            .catch((err: unknown) => console.error('Reloading permissions after a site package change failed', err))
            .finally(() => {
                this.permissionsReload = undefined;
                if (!this.permissionsReloadQueued) return;
                this.permissionsReloadQueued = false;
                this.requestPermissionsReload();
            });
    }

    /**
     * Re-fetches every loaded checker's permissions, since the server narrows grants to each site's packages, then announces it.
     * Before the first permissions load it only marks that load to be followed by a reload.
     * A reload that outlives a sign-out or an account switch is dropped, and each request names both so a shared in-flight GET
     * from before them is not reused.
     */
    private async reloadPermissions(): Promise<void> {
        if (!this.permissions) {
            this.reloadAfterFirstLoad = true;
            return;
        }
        const logouts = this.logouts;
        const loads = this.permissionsLoads;
        const stale = () => logouts !== this.logouts || loads !== this.permissionsLoads;
        const scope = { session: logouts, load: loads };
        const permissions = await this.httpService.get<PermissionApiResponse>(null, '/core/me/account/permissions-v2', scope);
        const accountIds = (this.accounts ?? []).map(account => account.id).filter(id => this.permissionCheckers[id]);
        const accounts = await Promise.allSettled(accountIds.map(async accountId =>
            [accountId, await this.httpService.get<PermissionApiResponse>(null, `/a/${accountId}/core/me/account/permissions-v2`, scope)] as const));
        if (stale()) return;
        this.permissions = permissions;
        this.permissionChecker = siteAwareChecker(permissions, permissions?.grants?.[0]?.scope.accountId);
        accounts.forEach(result => {
            if (result.status === 'rejected') console.error('Reloading an account\'s permissions failed', result.reason);
            else this.permissionCheckers[result.value[0]] = siteAwareChecker(result.value[1], result.value[0]);
        });
        this.permissionChange$.next(new Date());
    }

    async initAccountPermissionChecker(component, accountId: string, personId: string, update: boolean = false): Promise<void> {
        const perms = await this.httpService.get<PermissionApiResponse>(component, `/a/${accountId}/core/me/account/permissions-v2`);
        this.permissionCheckers[accountId] = siteAwareChecker(perms, accountId);
        if (update) this.permissionChange$.next(new Date());
        this.permissionSub.push(this.pubSubService.subscribe<Person>(component, Topic.AccountPersonPermissionsUpdated, [accountId, personId], false)
                    .subscribe(() => {
                        this.initAccountPermissionChecker(component, accountId, personId, true);
                    }));
    }

    async subscribeNotifications(component) {
        const profile = await this.getUserProfile(component);
        return await this.pubSubService.subscribe<PushPayload>(component, Topic.UserNotification, [profile.id]);
    }

    async subscribeBadgeUpdates(component) {
        const profile = await this.getUserProfile(component);
        return await this.pubSubService.subscribe<UserBadge>(component, Topic.UserBadgeUpdate, [profile.id]);
    }

    hasUserPermissionOnAnyAccount(action: PermissionAction) {
        return myUser().accountIds.some(accountId => this.hasPermissionInAnyAccountFacility(action, accountId));
    }

    hasPermissionInAnyAccountFacility(action: PermissionAction, accountId?: string) {
        if (!accountId) return Object.values(this.permissionCheckers).some(x => x.hasAnyPermission(action));
        if (!this.permissionCheckers[accountId]) return false;
        return this.permissionCheckers[accountId].hasAnyPermission(action);
    }

    hasPersonPermission(action: PermissionAction, accountId: string, person: PersonLike, isPersonOnFacility?: PersonFacilityPredicate, facilityId?: string) {
        return person && this.permissionCheckers[accountId]?.hasPersonPermission(action, person, isPersonOnFacility, facilityId);
    }

    hasFacilitiesPermission(action: PermissionAction, facilityIds?: string | string[], folderId?: string, allFacilities = false) {
        if (!action) return true;

        const facilityId = Array.isArray(facilityIds) ? null : facilityIds;
        const allSites = Array.isArray(facilityIds) && !facilityIds.length;
        if (this.hasPermission(action, facilityId, folderId, allSites) && this.isEntitledAtRequested(action, facilityIds, allFacilities)) return true;
        if (!Array.isArray(facilityIds)) return false;

        if (!facilityIds?.length) {
            return allFacilities ? false : this.hasPermissionInAnyFacility(action);
        }
        return facilityIds[allFacilities ? 'every' : 'some'](v => this.hasPermission(action, v, folderId));
    }

    /** Whether the account is entitled to `action` at the requested sites (all of them with `allFacilities`); true without a list. */
    private isEntitledAtRequested(action: PermissionAction, facilityIds: string | string[] | undefined, allFacilities: boolean): boolean {
        if (!this.permissions || !Array.isArray(facilityIds) || !facilityIds.length) return true;
        return facilityIds[allFacilities ? 'every' : 'some'](id => this.permissionChecker.isEntitledAt(action, id));
    }

    hasAddPermission(action: PermissionAction, facilityId?: string, multiFacility?: boolean, folderId?: string) {
        if (!action) return true;

        if (this.hasPermission(action, facilityId, folderId)) return true;
        if (!multiFacility) return false;
        return this.hasPermissionInAnyFacility(action);
    }

    /** @param entitledAccountWide check entitlement across the whole account, for something that applies to every site. */
    hasPermission(action: PermissionAction, facilityId?: string, folderId?: string, entitledAccountWide = false) {
        facilityId = facilityId ?? this.facilityService.getCurrentFacilityId();
        if (!this.permissions) return true;
        return this.permissionChecker.hasPermission(action, facilityId, folderId, entitledAccountWide);
    }

    hasPermissionInAnyFacility(action: PermissionAction) {
        if (!this.permissions) return true;
        return this.permissionChecker.hasPermissionInAnyFacility(action);
    }

    hasAnyPermission(action: PermissionAction, facilityId?: string, folderId?: string) {
        facilityId = facilityId ?? this.facilityService.getCurrentFacilityId();
        if (!this.permissions) return true;
        return this.permissionChecker.hasAnyPermission(action, facilityId, folderId);
    }

    hasAccountOrAnyFolderPermission(action: PermissionAction) {
        if (!this.permissions) return true;
        return this.permissionChecker.hasFacilityOrAnyFolderPermission(action, null);
    }

    /**
     * Administer Account as an account-wide page checks it: with `SiteScopedPackages` on, only account-wide grants count;
     * off, the selected site's grants count too, as before.
     */
    hasAccountAdminPermission(siteScopedPackages: boolean) {
        if (!siteScopedPackages) return this.hasPermission(PermissionAction.AdministerAccount);
        if (!this.permissions) return true;
        return this.permissionChecker.hasPermission(PermissionAction.AdministerAccount);
    }

    hasAnyPermissionInAnyFacility(action: PermissionAction) {
        if (!this.permissions) return true;
        return this.permissionChecker.hasAnyPermission(action);
    }

    hasAccountPermission(action: PermissionAction) {
        return this.permissionChecker.hasAccountPermission(action);
    }

    hasAccountPermissionInAnyAccount(action: PermissionAction) {
        if (!Object.values(this.permissionCheckers).length) return false;
        return Object.values(this.permissionCheckers).some(x => x.hasAccountPermission(action));
    }

    addFolder(id: string, parentId: string) {
        if (!this.permissions) return;
        this.permissionChecker.addFolder(id, parentId);
    }

    isMaster() {
        return this.accounts?.some(x => x.master);
    }

    async updateUserProfile(component, id: string, update: UpdateUser) {
        try {
            const updatedProfile = await this.httpService.put<UserProfile>(component, `/core/users/${id}`, update);
            this.profileUpdateSubject.next(updatedProfile);
            this.userProfile = updatedProfile;
            return updatedProfile;
        } catch (e) {
            this.alertsService.sendError({ error: e, messageKey: 'ERRORS.PROFILE.UPDATE' });
            this.alertsService.setAppLoading(false);

            throw (e);
        }
    }

    async uploadAvatar(component, fd: FormData): Promise<UploadResponse> {
        try {
            fd.append('mirrored', 'true');
            return await this.httpService.upload<UploadResponse>(component, '/core/uploads/avatar', fd);
        } catch (e) {
            if (e.error?.details?.reason === 'file_too_large') {
                this.alertsService.sendError({ error: e, messageKey: 'ERRORS.FILE.TOO-LARGE' });
            } else if (e.error?.details?.reason === 'no_face') {
                this.alertsService.sendError({ error: e, messageKey: 'ERRORS.FILE.NO-FACE' });
            } else {
                this.alertsService.sendError({ error: e, messageKey: 'ERRORS.UPLOAD' });
            }
            this.alertsService.setAppLoading(false);

            throw (e);
        }
    }

    async getQrLogin(component, id: string): Promise<UserQrLogin> {
        try {
            return await this.httpService.get(component, `/core/users/${id}/qr-login`);
        } catch (e) {
            console.error(e);
        }
    }

    async generateNewQrLogin(component, id: string): Promise<UserQrLogin> {
        try {
            return await this.httpService.put(component, `/core/users/${id}/generate-new-qr-login`, {});
        } catch (e) {
            console.error(e);
        }
    }

    async resetQrPin(component, id: string, pin?: string): Promise<UserQrLogin> {
        try {
            return await this.httpService.put(component, `/core/users/${id}/reset-qr-pin`, { pin });
        } catch (e) {
            console.error(e);
        }
    }

    async adminResetPin(component, request: ResetPinRequest) {
        return this.httpService.post<any>(component, '/account/admin-reset-pin', request);
    }
}

/** A checker whose catalog-gated actions follow the account's synced site packages; without an account, `enabledActions` decides. */
function siteAwareChecker(permissions: PermissionApiResponse, accountId: string | undefined): PermissionChecker {
    return new PermissionChecker(permissions, accountId ? facilityId => siteEntitlements(accountId, facilityId) : undefined);
}
